/**
 * Maps a position reported by the speech engine back onto a word on the page.
 *
 * The engine speaks `ttsText`, not what the reader sees: cleanTextForTTS expands
 * "dr." to "doktor", turns 01.03.2024 into three Polish words, drops heading and
 * emphasis markers, and collapses whitespace. So the `charIndex` that arrives on a
 * boundary event indexes a string the reader has never seen, and highlighting by
 * that offset directly would drift a word or two into any chunk containing a
 * number, an abbreviation or a marker.
 *
 * Rather than thread source offsets through every rewrite in that pipeline, the
 * two token sequences are aligned after the fact. They are near-identical - a
 * handful of substitutions in a chunk of a dozen words - so a longest common
 * subsequence recovers the correspondence exactly where the words survived, and
 * the substituted runs are distributed proportionally across the source words
 * they came from. "doktor" lands on "dr.", and all three words of a spoken date
 * land on the single "01.03.2024" that produced them.
 */

export interface WordToken {
  text: string
  /** Offset of the token's first character within the string it came from. */
  start: number
  /** Offset just past the token's last character. */
  end: number
}

/** Whitespace-separated tokens, with their offsets. */
export function tokenizeWords(text: string): WordToken[] {
  const tokens: WordToken[] = []
  const pattern = /\S+/g
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    tokens.push({ text: match[0], start: match.index, end: match.index + match[0].length })
  }

  return tokens
}

/**
 * Reduces a token to what two spellings of the same word have in common:
 * case and surrounding punctuation are dropped, the letters and digits are not.
 */
function normalizeToken(token: string): string {
  return token
    .toLowerCase()
    .replace(/^[^0-9a-zà-ɏ]+/, '')
    .replace(/[^0-9a-zà-ɏ]+$/, '')
}

/**
 * Matched (display, spoken) index pairs, in order, from a longest common
 * subsequence of the two normalized token lists.
 *
 * Chunks are at most a couple of dozen words (see MAX_CHUNK_WORDS), so the
 * quadratic table is a few hundred cells and is built once per chunk.
 */
function findMatchingPairs(displayWords: string[], spokenWords: string[]): Array<[number, number]> {
  const rows = displayWords.length
  const columns = spokenWords.length

  // A token that normalizes to nothing - a lone dash, a stray bullet - must not
  // match another such token, so those compare as never equal.
  const matches = (row: number, column: number): boolean => {
    const display = displayWords[row]
    return display.length > 0 && display === spokenWords[column]
  }

  const lengths: number[][] = Array.from({ length: rows + 1 }, () => new Array(columns + 1).fill(0))

  for (let row = rows - 1; row >= 0; row -= 1) {
    for (let column = columns - 1; column >= 0; column -= 1) {
      lengths[row][column] = matches(row, column)
        ? lengths[row + 1][column + 1] + 1
        : Math.max(lengths[row + 1][column], lengths[row][column + 1])
    }
  }

  const pairs: Array<[number, number]> = []
  let row = 0
  let column = 0

  while (row < rows && column < columns) {
    if (matches(row, column)) {
      pairs.push([row, column])
      row += 1
      column += 1
    } else if (lengths[row + 1][column] >= lengths[row][column + 1]) {
      row += 1
    } else {
      column += 1
    }
  }

  return pairs
}

/**
 * For each spoken word, the index of the display word it should highlight.
 */
export function mapSpokenWordsToDisplayWords(
  displayWords: string[],
  spokenWords: string[]
): number[] {
  if (spokenWords.length === 0) return []
  if (displayWords.length === 0) return new Array(spokenWords.length).fill(0)

  const normalizedDisplay = displayWords.map(normalizeToken)
  const normalizedSpoken = spokenWords.map(normalizeToken)
  const pairs = findMatchingPairs(normalizedDisplay, normalizedSpoken)

  const mapping = new Array<number>(spokenWords.length).fill(0)
  const lastDisplayIndex = displayWords.length - 1

  let previousDisplay = -1
  let previousSpoken = -1

  // The sentinel pair past both ends closes the final gap with the same code.
  for (const [display, spoken] of [...pairs, [displayWords.length, spokenWords.length]]) {
    const spokenGap = spoken - previousSpoken - 1
    const displayGapStart = previousDisplay + 1
    const displayGapLength = display - previousDisplay - 1

    for (let offset = 0; offset < spokenGap; offset += 1) {
      // Spoken words with no counterpart at all - the "Tekst z obrazu" prefix, a
      // spelled-out number the source wrote in digits - are parked on the display
      // word the run is heading towards, which keeps the highlight moving forward
      // rather than stalling on the previous word.
      const target =
        displayGapLength > 0
          ? displayGapStart + Math.floor((offset * displayGapLength) / spokenGap)
          : Math.min(displayGapStart, lastDisplayIndex)

      mapping[previousSpoken + 1 + offset] = Math.min(Math.max(target, 0), lastDisplayIndex)
    }

    if (spoken < spokenWords.length) {
      mapping[spoken] = Math.min(display, lastDisplayIndex)
    }

    previousDisplay = display
    previousSpoken = spoken
  }

  return mapping
}

export interface ChunkAlignment {
  /** Tokens of the string handed to the speech engine, with their offsets in it. */
  spokenTokens: WordToken[]
  /** For each spoken token, the index of the display word to highlight. */
  spokenToDisplay: number[]
  /** How many words the reader sees in this chunk. */
  displayWordCount: number
}

export function createChunkAlignment(displayText: string, spokenText: string): ChunkAlignment {
  const displayTokens = tokenizeWords(displayText)
  const spokenTokens = tokenizeWords(spokenText)

  return {
    spokenTokens,
    spokenToDisplay: mapSpokenWordsToDisplayWords(
      displayTokens.map((token) => token.text),
      spokenTokens.map((token) => token.text)
    ),
    displayWordCount: displayTokens.length,
  }
}

/**
 * Turns a boundary event's character offset into a display word index.
 *
 * Engines disagree about where inside a word the offset points - Chromium reports
 * the first character, others land mid-token - so the answer is the last token
 * that starts at or before the offset.
 */
export function displayWordIndexForSpokenCharIndex(
  alignment: ChunkAlignment,
  charIndex: number
): number {
  const { spokenTokens, spokenToDisplay } = alignment

  if (spokenTokens.length === 0) return 0

  let low = 0
  let high = spokenTokens.length - 1
  let found = 0

  while (low <= high) {
    const middle = (low + high) >> 1
    if (spokenTokens[middle].start <= charIndex) {
      found = middle
      low = middle + 1
    } else {
      high = middle - 1
    }
  }

  return spokenToDisplay[found] ?? 0
}

const EMPHASIS_MARKER = '**'

/**
 * Tokens that are markup rather than words: heading hashes, a bullet asterisk,
 * and either half of an image block.
 *
 * The image markers joined this list with DYS-045. The block is reassembled for
 * display one level up (chunked-text-runs.ts), which leaves the marker sitting
 * inside the chunk's own text - the chunker's sentence range starts at the `[` -
 * with nothing else to hide it. Like the others it keeps its place in the list
 * rather than being dropped, because the word index the manager reports counts
 * it.
 */
const MARKER_ONLY_TOKEN = /^(?:#+|\*|\[OBRAZ\]|\[\/OBRAZ\])$/

export interface DisplayWord {
  /** What the reader sees - markers stripped. Empty for a marker-only token. */
  text: string
  emphasized: boolean
  /**
   * The whitespace this word followed inside its chunk. Preserved rather than
   * normalised to a single space because the reading pane renders with
   * `white-space: pre-wrap`, so this is visible layout.
   */
  gapBefore: string
}

/**
 * Splits one speech chunk into the words to be highlighted individually.
 *
 * This is the display half of the contract above: the word index the manager
 * reports is an index into exactly this list, so both sides tokenise the chunk's
 * raw text the same way - hence tokenizeWords, and hence marker-only tokens
 * keeping their place with empty text rather than being dropped. Removing one
 * would shift every word after it by one.
 *
 * Emphasis state is carried across tokens, so `**dwa słowa**` renders both words
 * bold even though neither token holds a complete pair of markers.
 */
export function splitDisplayWords(chunkText: string): DisplayWord[] {
  const tokens = tokenizeWords(chunkText)
  let emphasized = false
  let previousEnd = 0

  return tokens.map((token) => {
    const gapBefore = chunkText.slice(previousEnd, token.start)
    previousEnd = token.end

    let text = ''
    let tokenEmphasized = emphasized
    let index = 0

    while (index < token.text.length) {
      if (token.text.startsWith(EMPHASIS_MARKER, index)) {
        emphasized = !emphasized
        if (emphasized) {
          tokenEmphasized = true
        }
        index += EMPHASIS_MARKER.length
        continue
      }
      text += token.text[index]
      index += 1
    }

    return {
      text: MARKER_ONLY_TOKEN.test(text) ? '' : text,
      emphasized: tokenEmphasized,
      gapBefore,
    }
  })
}
